import { STRATEGY_OPTION } from 'enums';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { CRYPTO_PATH, SELECT_SLIM_HEIGHT, THEME } from '../../../../../constants';

const { PUBLIC_URL } = process.env;

import { useCallback } from 'react';
import { useParams } from 'react-router';
import { CURRENCY } from '@voladeum/client';
import classNames from 'classnames';
import { useClientContext } from 'client/context';
import { CandleData } from 'client/voladeum/candles/types';
import { FORMAT_TYPE } from 'cyprus/enums';
import { formatValue } from 'cyprus/formats';
import { updateModal } from 'ducks/modals/slices';
import { selectSettings } from 'ducks/settings/selectors';
import { Dropdown } from 'framework/dropdown';
import { useConfirmModal } from 'hooks/useConfirmModal';
import { useStrategyContext } from 'pages/context/StrategyContext';
import { useStrategyApi } from 'pages/hooks/useStrategyApi';

import { Card } from 'components/cards/Card';
import { strategy_options } from 'components/cells/constants';

import { StrategyOption } from '../../types';

import cn from './Information.module.scss';

export const StrategyInformationSlim = () => {
  const dispatch = useAppDispatch();
  const { strategy_id } = useParams();
  const { current, previous } = useAppSelector(selectSettings);
  const { strategy } = useStrategyContext();
  const { getStrategyLookups } = useStrategyApi();

  const confirmModal = useConfirmModal();
  const { Voladeum } = useClientContext();

  const onSelect = useCallback(
    async (option: StrategyOption) => {
      switch (option.id) {
        case STRATEGY_OPTION.ACTIVATE_STRATEGY:
          confirmModal({
            title: option.name,
            message: option.message,
            onConfirm: () => Voladeum.pairs.activate(strategy_id),
          });
          break;
        case STRATEGY_OPTION.UPDATE_STRATEGY:
          await getStrategyLookups();
          dispatch(
            updateModal({ key: 'strategy_manage', data: { is_open: true, is_update: true } })
          );
          break;
        case STRATEGY_OPTION.PUBLISH_STRATEGY:
          dispatch(updateModal({ key: 'candle_workers', data: { is_open: true } }));
          break;
        case STRATEGY_OPTION.ARCHIVE_STRATEGY:
          confirmModal({
            title: option.name,
            message: option.message,
            onConfirm: () => Voladeum.pairs.archive(strategy_id),
          });
          break;
      }
    },
    [confirmModal, getStrategyLookups, dispatch, Voladeum.pairs, strategy_id]
  );

  const isPositive = useCallback(
    (key: keyof CandleData) => {
      if (current && previous) {
        return current[key] > previous[key];
      }
      return false;
    },
    [current, previous]
  );

  const isNegative = useCallback(
    (key: keyof CandleData) => {
      if (current && previous) {
        return current[key] < previous[key];
      }
      return false;
    },
    [current, previous]
  );

  const { pair_name, adapter_name, base_currency_name, base_currency_id, since_utc_formatted } =
    strategy;
  const currency = CRYPTO_PATH[base_currency_id as CURRENCY];

  return (
    <Card maxHeight="fit-content">
      <div className={cn.card}>
        <div className={cn.crypto}>
          <img
            className={cn.img}
            src={`${PUBLIC_URL}/cryptocurrencies/${currency}.svg`}
            alt={base_currency_name}
          />
          <div className={cn.section}>
            <p>{pair_name}</p>
            <p className={cn.small}>{adapter_name}</p>
          </div>
        </div>
        <div className={cn.section}>
          <p>{since_utc_formatted}</p>
          <p className={cn.small}>Date</p>
        </div>
        <div className={cn.section}>
          <p>
            {formatValue(current?.x, { format: FORMAT_TYPE.UNIX_TIME, invalid: '-', utc: true })}
          </p>
          <p className={cn.small}>Time</p>
        </div>
        <div className={cn.section}>
          <p className={classNames({ [cn.green]: isPositive('o'), [cn.red]: isNegative('o') })}>
            {formatValue(current?.o, { format: FORMAT_TYPE.CURRENCY, invalid: '-' })}
          </p>
          <p className={cn.small}>Open</p>
        </div>
        <div className={cn.section}>
          <p className={classNames({ [cn.green]: isPositive('h'), [cn.red]: isNegative('h') })}>
            {formatValue(current?.h, { format: FORMAT_TYPE.CURRENCY, invalid: '-' })}
          </p>
          <p className={cn.small}>High</p>
        </div>
        <div className={cn.section}>
          <p className={classNames({ [cn.green]: isPositive('l'), [cn.red]: isNegative('l') })}>
            {formatValue(current?.l, { format: FORMAT_TYPE.CURRENCY, invalid: '-' })}
          </p>
          <p className={cn.small}>Low</p>
        </div>
        <div className={cn.section}>
          <p className={classNames({ [cn.green]: isPositive('c'), [cn.red]: isNegative('c') })}>
            {formatValue(current?.c, { format: FORMAT_TYPE.CURRENCY, invalid: '-' })}
          </p>
          <p className={cn.small}>Close</p>
        </div>
        <div className={cn.section}>
          <p className={classNames({ [cn.green]: isPositive('v'), [cn.red]: isNegative('v') })}>
            {formatValue(current?.v, { format: FORMAT_TYPE.NUMBER, decimal: 5, invalid: '-' })}
          </p>
          <p className={cn.small}>Volume</p>
        </div>
        <div className={cn.flex} />
        <Dropdown
          value="Actions"
          options={strategy_options}
          onSelect={onSelect}
          option_key="name"
          height={SELECT_SLIM_HEIGHT}
          theme={THEME.select.slim}
          right_icon={null}
          extend
          autosize
        />
      </div>
    </Card>
  );
};
